import i18next from 'i18next';
import * as React from 'react';
import { Form } from 'react-bootstrap';
import { StandardLocalWidget, any, fromJSX } from "widgets-for-react";
import { getClassName } from '../../authentication/auth_widget/utils';
import { DataLayer, PIIData } from '../../utils/datalayer';
import { FormControls, formSkeletonConfig } from "../../utils/forms";
import { ComponentDidMount } from '../../utils/lifecycleMethods';
import { capitalize } from '../../utils/utils';
import { Country } from "../country/countryState";
import { countryWidget } from "../country/countryWidget";
import { GraduationYearLayout } from '../graduationYear/graduationYearLayout';
import { Job } from "../job/jobState";
import { jobWidget } from "../job/jobWidget";
import { Language } from "../language/languageState";
import { languageWidget } from "../language/languageWidget";
import { PersonalInfo, PersonalInfoState, availableGenders, personalInfoStateUpdaters, personalInfoUpdaters } from "./personalInfoState";


const formatDateYYYYMMDD = (yourDate: Date) => {
  const offset = yourDate.getTimezoneOffset()
  yourDate = new Date(yourDate.getTime() - (offset * 60 * 1000))
  return yourDate.toISOString().split('T')[0]
}

export const personalInfoWidget = (availableLanguages: undefined | Array<Language>, availableCountries: Array<Country>, availableJobs: Array<Job>, readOnly: boolean, countryReadOnly: boolean, inUpgradeFlow: boolean, isStudent: boolean, suppressHotjar: boolean, piiData?: PIIData): StandardLocalWidget<PersonalInfoState> => currentState => {
  const graduationYear = currentState.input.graduationYear == null ? undefined : currentState.input.graduationYear <= 0 ? -1 : currentState.input.graduationYear
  return any([
    currentState.validator.widget().map(personalInfoStateUpdaters.validation),
    fromJSX(setState =>
      <>
        <FormControls.ShowIf condition={!readOnly || currentState.input.name != undefined}>
          <ComponentDidMount onMount={() => {if(inUpgradeFlow && currentState.input.name) DataLayer.upgradeFlowStep2("name", piiData)}}/>
          <FormControls.FormItemInGroup
            controlId="firstName"
            label={i18next.t("auth:signupName")}
            type="text"
            required={true}
            readOnly={readOnly}
            value={currentState.input.name || ""}
            onChange={(newValue => setState(personalInfoStateUpdaters.input(personalInfoUpdaters.name(_ => newValue))))}
            onBlur={() => { if(inUpgradeFlow) DataLayer.upgradeFlowStep2("name", piiData) }}
            validation={currentState.validatorResult.path("name")}
            suppressHotjar={suppressHotjar}
          />
        </FormControls.ShowIf>
        <FormControls.ShowIf condition={!readOnly || currentState.input.surname != undefined}>
          <ComponentDidMount onMount={() => {if(inUpgradeFlow && currentState.input.surname) DataLayer.upgradeFlowStep2("surname", piiData)}}/>
          <FormControls.FormItemInGroup
            controlId="surname"
            label={i18next.t("auth:signupSurname")}
            type="text"
            required={true}
            readOnly={readOnly}
            value={currentState.input.surname || ""}
            onChange={(newValue => setState(personalInfoStateUpdaters.input(personalInfoUpdaters.surname(_ => newValue))))}
            onBlur={() => { if(inUpgradeFlow) DataLayer.upgradeFlowStep2("surname", piiData) }}
            validation={currentState.validatorResult.path("surname")}
            suppressHotjar={suppressHotjar}
          />
        </FormControls.ShowIf>
        <FormControls.ShowIf condition={!inUpgradeFlow && (!readOnly || (currentState.input.phoneNumber != undefined && currentState.input.phoneNumber != ''))}>
          <FormControls.FormItemInGroup
            key="subscriptionPhoneNumber"
            controlId="telNumber"
            label={i18next.t("subscription:PhoneNumber")}
            type="text"
            required={false}
            readOnly={readOnly}
            inputMode="tel"
            value={currentState.input.phoneNumber || ""}
            onChange={(newValue => setState(personalInfoStateUpdaters.input(personalInfoUpdaters.phoneNumber(_ => newValue))))}
            validation={currentState.validatorResult.path("phoneNumber")}
            suppressHotjar={suppressHotjar}
          />
        </FormControls.ShowIf>
        <FormControls.ShowIf condition={!inUpgradeFlow && (!readOnly || currentState.input.dateOfBirth != undefined)}>
          <FormControls.FormItemInGroup
            key="subscriptionBirthday"
            controlId="birthday"
            label={i18next.t("subscription:DateOfBirth")}
            required={false}
            type="date"
            readOnly={readOnly}
            inputMode="numeric"
            value={formatDateYYYYMMDD(currentState.input.dateOfBirth || new Date(Date.now()))}
            onChange={(newValue => setState(personalInfoStateUpdaters.input(personalInfoUpdaters.dateOfBirth(_ => {
              try {
                const newDate = new Date(newValue)
                const attemptedFormatting = formatDateYYYYMMDD(newDate)
                return newDate
              } catch (error) {
                return _
              }
            }))))}
            validation={currentState.validatorResult.path("email")}
            suppressHotjar={suppressHotjar}
          />
        </FormControls.ShowIf>
        <FormControls.ShowIf condition={!inUpgradeFlow && (!readOnly || currentState.input.gender != undefined)}>
          <>
            {readOnly ? (
              currentState.input.gender != undefined && (
                <>
                  <Form.Label className={getClassName(formSkeletonConfig.options, 'formLabelClassName', '')}>
                    {i18next.t('subscription:Gender')}
                  </Form.Label>
                  <p className='data-hj-suppress'>
                    {currentState.input.gender != undefined && i18next.t(`subscription:${currentState.input.gender != 'other'
                      ? capitalize(currentState.input.gender)
                      : 'Unknown'}`)}
                  </p>
                </>
              )
            ) : (
              <FormControls.FormRadioInGroup
                name="gender"
                legend={i18next.t("subscription:Gender")}
                required={false}
                readOnly={readOnly}
                validation={undefined}
                items={availableGenders.map(gender => ({
                  controlId: gender ? `subscriptionGender${capitalize(gender)}` : '',
                  label: gender ? i18next.t(`subscription:${gender != 'other' ? capitalize(gender) : 'Unknown'}`) : '',
                  checked: currentState.input.gender && currentState.input.gender == gender || false,
                  onChange: () => setState(personalInfoStateUpdaters.input(personalInfoUpdaters.gender(_ => gender as PersonalInfo["gender"])))
                }))}
                suppressHotjar={suppressHotjar}
              />
            )}
          </>
        </FormControls.ShowIf>
        {jobWidget(availableJobs, true, readOnly, inUpgradeFlow ? "upgrade" : "none", isStudent, suppressHotjar )(currentState.jobState)
          .map(personalInfoStateUpdaters.jobState)
          .run(setState)}
        {countryWidget(
          availableCountries,
          suppressHotjar,
          readOnly || countryReadOnly,
          countryReadOnly ? i18next.t('subscription:ChangeCountryText') : undefined,
          inUpgradeFlow,
          piiData
        )(currentState.countryState)
          .map(personalInfoStateUpdaters.countryState)
          .run(setState)} 
        {availableLanguages && languageWidget(availableLanguages, readOnly, suppressHotjar)(currentState.languageState)
          .map(personalInfoStateUpdaters.languageState)
          .run(setState)}     
        <FormControls.ShowIf condition={isStudent && !inUpgradeFlow && (!readOnly || (!!currentState.input.graduationYear))}>
          <GraduationYearLayout
            readOnly={readOnly}
            value={graduationYear}
            onChange={(newValue => setState(personalInfoStateUpdaters.input(personalInfoUpdaters.graduationYear(_ => parseInt(newValue)))))}
            isValid={!!graduationYear}
            suppressHotjar={suppressHotjar}
          />
        </FormControls.ShowIf>
      </>
    )
  ])
}

